import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar, SpinLoading, Picker, SearchBar } from 'antd-mobile';
import { DownOutline } from 'antd-mobile-icons';
import { getNavBarContainerStyle } from '../../../utils/deviceUtils';
import { useGetAreaScanStatistics, AreaParcelInfoType } from '../graphql/queries/getAreaScanStatistics';
import { t } from '../../../i18n';
import { TaskPath } from "../../../enums/DeliveryNavigatePathEnum";
import { getExpandableText } from '../../../utils/textUtils';

/**
 * 分组后的区域数据
 */
interface GroupedAreaData {
  areaName: string;
  parcels: AreaParcelInfoType[];
}

/**
 * 区域扫描明细页面
 */
const AreaScanDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useGetAreaScanStatistics();

  // 当前选中的区域和Tab
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'scanned' | 'unscanned'>('scanned');
  const [pickerVisible, setPickerVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');
  // 记录展开的行（使用 trackingNumber 作为标识）
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // 数据分组和排序
  const groupedData = useMemo<GroupedAreaData[]>(() => {
    if (!data?.getAreaScanStatistics?.parcelList) {
      return [];
    }

    const parcelList = data.getAreaScanStatistics.parcelList;

    // 按 areaName 分组
    const groupMap = new Map<string, AreaParcelInfoType[]>();
    parcelList.forEach(parcel => {
      const area = parcel.areaName || t('pages.delivery.task.areaScanDetail.unassignedArea');
      if (!groupMap.has(area)) {
        groupMap.set(area, []);
      }
      groupMap.get(area)!.push(parcel);
    });

    // 转换为数组并排序区域名称（自然排序）
    const result: GroupedAreaData[] = Array.from(groupMap.entries())
      .map(([areaName, parcels]) => {
        // 对区域内的包裹按 stopNumber + subStopNumber 排序
        const sortedParcels = [...parcels].sort((a, b) => {
          // 先按 stopNumber 排序
          if (a.stopNumber !== b.stopNumber) {
            return a.stopNumber - b.stopNumber;
          }
          // stopNumber 相同时按 subStopNumber 排序
          return a.subStopNumber.localeCompare(b.subStopNumber, undefined, { numeric: true });
        });

        return {
          areaName,
          parcels: sortedParcels,
        };
      })
      .sort((a, b) => {
        // 区域名称自然排序
        return a.areaName.localeCompare(b.areaName, undefined, { numeric: true });
      });

    return result;
  }, [data]);

  // 设置默认选中第一个区域
  React.useEffect(() => {
    if (groupedData.length > 0 && !selectedArea) {
      setSelectedArea(groupedData[0].areaName);
    }
  }, [groupedData, selectedArea]);

  const areaPickerColumns = useMemo(() => {
    return [
      groupedData.map(area => ({
        label: area.areaName,
        value: area.areaName,
      }))
    ];
  }, [groupedData]);

  // 获取当前选中区域的数据
  const currentAreaData = useMemo(() => {
    return groupedData.find(item => item.areaName === selectedArea);
  }, [groupedData, selectedArea]);

  // 计算当前选中区域的已扫描和未扫描数量
  const currentAreaStats = useMemo(() => {
    if (!currentAreaData) {
      return { scannedCount: 0, unscannedCount: 0, areaTotal: 0 };
    }

    const scannedCount = currentAreaData.parcels.filter(p => p.isPickedUp).length;
    const unscannedCount = currentAreaData.parcels.filter(p => !p.isPickedUp).length;
    const areaTotal = currentAreaData.parcels.length || 0;

    return { scannedCount, unscannedCount, areaTotal };
  }, [currentAreaData]);

  // 获取当前Tab显示的包裹列表（根据Tab和搜索条件筛选）
  const displayParcels = useMemo(() => {
    if (!currentAreaData) return [];

    // 先根据Tab筛选
    let filteredParcels = activeTab === 'scanned'
      ? currentAreaData.parcels.filter(p => p.isPickedUp)
      : currentAreaData.parcels.filter(p => !p.isPickedUp);

    // 再根据搜索条件筛选
    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase().trim();
      filteredParcels = filteredParcels.filter(parcel =>
        parcel.trackingNumber.toLowerCase().includes(searchLower)
      );
    }

    return filteredParcels;
  }, [currentAreaData, activeTab, searchValue]);

  const handleBack = () => {
    navigate(TaskPath.DELIVERY_TASK_ASSIGNMENTS);
  };

  // 切换行的展开/收起状态
  const toggleRowExpanded = (trackingNumber: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(trackingNumber)) {
        newSet.delete(trackingNumber);
      } else {
        newSet.add(trackingNumber);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-white">
        <div {...getNavBarContainerStyle()}>
          <NavBar onBack={handleBack}>
            <span className="font-bold">{t('pages.delivery.task.areaScanDetail.title')}</span>
          </NavBar>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <SpinLoading color="primary" style={{ '--size': '48px' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-screen bg-white">
        <div {...getNavBarContainerStyle()}>
          <NavBar onBack={handleBack}>
            <span className="font-bold">{t('pages.delivery.task.areaScanDetail.title')}</span>
          </NavBar>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <SpinLoading color="primary" style={{ '--size': '48px' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* 导航栏 */}
      <div {...getNavBarContainerStyle()} className="bg-white">
        <NavBar onBack={handleBack}>
          <span className="font-bold">{t('pages.delivery.task.areaScanDetail.title')}</span>
        </NavBar>
      </div>

      {/* 统计信息 */}
      <div className="bg-white px-4 py-3">
        <div className="text-sm text-gray-600 mb-2 font-bold">
          <div className="flex items-center justify-between mb-3">
            <div>
              {t('pages.delivery.task.areaScanDetail.routeTotalParcels')}{currentAreaStats.areaTotal}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">{t('pages.delivery.task.areaScanDetail.area')}</span>
              <div
                  className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded bg-white active:bg-gray-50 cursor-pointer"
                  onClick={() => setPickerVisible(true)}
              >
                <span className="text-sm text-gray-700">{selectedArea}</span>
                <DownOutline fontSize={12} className="text-gray-400" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex-1 bg-purple-50 rounded-lg p-4 text-center">
            <div className="text-sm text-black mb-1 font-bold">{t('pages.delivery.task.areaScanDetail.scanned')}</div>
            <div className="text-2xl font-semibold text-gray-600">{currentAreaStats.scannedCount}</div>
          </div>
          <div className="flex-1 bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-sm text-black mb-1 font-bold">{t('pages.delivery.task.areaScanDetail.unscanned')}</div>
            <div className="text-2xl font-semibold text-gray-600">{currentAreaStats.unscannedCount}</div>
          </div>
        </div>
      </div>

      {/* 包裹扫描明细标题和区域选择器 */}
      <div className="bg-white px-4 py-3 mt-2 border-b border-gray-200">
        <div className="flex items-center justify-between mb-1">
          <span className="text-base font-medium">{t('pages.delivery.task.areaScanDetail.parcelScanDetail')}</span>
        </div>
        {/* 搜索框 */}
        < div className = "py-2 border-b border-gray-200" >
          <SearchBar
            placeholder={t('pages.delivery.task.areaScanDetail.searchPlaceholder')}
            value={searchValue}
            onChange={setSearchValue}
          />
        </div>

        {/* Tab切换 */}
        <div className="flex border-b border-gray-200">
          <button
            className={`flex-1 pb-2 text-sm font-medium transition-colors ${
              activeTab === 'scanned'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500'
            }`}
            onClick={() => setActiveTab('scanned')}
          >
            {t('pages.delivery.task.areaScanDetail.scanned')}
          </button>
          <button
            className={`flex-1 pb-2 text-sm font-medium transition-colors ${
              activeTab === 'unscanned'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500'
            }`}
            onClick={() => setActiveTab('unscanned')}
          >
            {t('pages.delivery.task.areaScanDetail.unscanned')}
          </button>
        </div>
      </div>

      {/* 表头 */}
      <div className="p-1 border-b border-gray-200">
        <div className="grid grid-cols-12 mb-1 font-medium text-gray-500">
          <div className="col-span-7 px-1 text-center break-words">{t('pages.delivery.task.areaScanDetail.trackingNumber')}</div>
          <div className="col-span-2 px-1 text-center break-words">{t('pages.delivery.task.areaScanDetail.stop')}</div>
          <div className="col-span-3 px-1 text-center break-words">{activeTab === 'scanned' ? t('pages.delivery.task.areaScanDetail.lastScanner') : t('pages.delivery.task.areaScanDetail.assignedDriver')}</div>
        </div>
      </div>

      {/* 包裹列表 */}
      <div className="flex-1 overflow-y-auto bg-white">
        {displayParcels.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400 text-sm">{t('pages.delivery.task.areaScanDetail.noData')}</div>
          </div>
        ) : (
          displayParcels.map((parcel, index) => {
            // 组合 stop 显示
            const stopDisplay = parcel.subStopNumber
              ? `${parcel.stopNumber}-${parcel.subStopNumber}`
              : `${parcel.stopNumber}`;

            //const scannerName = parcel.lastScannerName || '-';
              const scannerName = activeTab === 'scanned'
                  ? (parcel.lastScannerName || '-')
                  : (parcel.allocationDriverName || '-');
            const isExpanded = expandedRows.has(parcel.trackingNumber);
            // 使用工具函数获取可展开文本（超过8个字符截断）
            const { displayText: displayName, needsTruncate } = getExpandableText(scannerName, isExpanded, 7);

            return (
                <div key={`${parcel.trackingNumber}-${index}`}
                     className="grid grid-cols-12 py-3 border-b border-gray-100">
                  <div className="col-span-7 px-1 font-mono text-sm text-center break-all">
                    {parcel.trackingNumber}
                  </div>
                  <div className="col-span-2 px-1 font-mono text-sm text-center break-words">
                    {stopDisplay}
                  </div>
                  <div
                    className={`col-span-3 px-1 font-mono text-sm text-center break-words ${
                      needsTruncate ? 'cursor-pointer active:bg-gray-100' : ''
                    }`}
                    onClick={() => needsTruncate && toggleRowExpanded(parcel.trackingNumber)}
                  >
                    {displayName}
                  </div>
                </div>
            );
          })
        )}
      </div>

      {/* 区域选择 Picker */}
      <Picker
        columns={areaPickerColumns}
        visible={pickerVisible}
        onClose={() => setPickerVisible(false)}
        onConfirm={(value) => {
          setSelectedArea(value[0] as string);
          setPickerVisible(false);
        }}
        value={[selectedArea]}
        title={t('pages.delivery.task.areaScanDetail.parcelScanDetail')}
      />
    </div>
  );
};

export default AreaScanDetailPage;
